import { UploadOutput, UploaderOptions } from 'ngx-uploader';
import { of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import {
  Input,
  Output,
  Component,
  OnDestroy,
  OnInit,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';

import { SnUser } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth';
import { SnCollection } from '@lib/common/models';

import { SnChannel, SnChatUser } from '../../models';
import { SnChatService, SnMembersService, SnMessagesService } from '../../services';

@Component({
  selector: 'sn-chat-channel',
  templateUrl: './chat-channel.container.html',
  styleUrls: ['./chat-channel.container.scss'],
})
export class SnChannelContainer implements OnInit, OnDestroy {
  @Input()
  public channel: SnChannel;

  @Input()
  public userHasAccess: boolean;

  @Output()
  public readonly channelChange = new EventEmitter<SnChannel>();

  public user: SnUser;
  public readonly uploaderOptions: UploaderOptions = { concurrency: 1, maxUploads: 10 };

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _membersService: SnMembersService,
    private readonly _chatService: SnChatService,
    private readonly _messagesService: SnMessagesService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.user = this._currentUser;
  }

  public ngOnDestroy(): void {
    this.channelChange.complete();
    this._chatService.resetActiveChannel();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public onUploadOutput($event: UploadOutput): void {
    if ($event.type === 'addedToQueue' && $event.file) {
      this._messagesService
        .sendMediaMessage(this.channel, $event.file.nativeFile)
        .pipe(takeUntil(this._destroy$))
        .subscribe();
    }
  }

  public join(): void {
    this._membersService
      .joinToChannel(this.channel.sid, this.channel.type)
      .pipe(
        switchMap((c) => {
          return this._chatService.getTwilioChannel(c.sid).pipe(
            switchMap((tc) => {
              c.twilioChannel = tc;
              return of(c);
            }),
          );
        }),
        switchMap((c) => {
          return this._membersService.fetchMemberList(c.sid, { pageSize: 'all' }).pipe(
            switchMap((res) => {
              c.members = new SnCollection<SnChatUser>(...res.results);
              return of(c);
            }),
          );
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((channel) => {
        this._onChannelUpdate(channel);
        this._cd.detectChanges();
      });
  }

  private _onChannelUpdate(channel: SnChannel): void {
    this.channel.publicity = channel.publicity;
    this.channel.hideSystemNotifications = channel.hideSystemNotifications;
    this.channel.displayName = channel.displayName;
    this.channel.users = channel.members;
    this.channel.twilioChannel = channel.twilioChannel;
    this._chatService.userJoinedChange();
    this.channelChange.emit(this.channel);
    this._loadChannel();
    this._cd.detectChanges();
  }

  private _loadChannel(): void {
    this._chatService.setActiveChannel(this.channel.sid);
    this.channel = this._chatService.activeChannel;
    this._checkUserAccess();
    this._cd.detectChanges();
  }

  private _checkUserAccess(): void {
    this._membersService
      .fetchMemberList(this.channel.sid, { pageSize: 'all' })
      .pipe(takeUntil(this._destroy$))
      .subscribe((value) => {
        this.channel.members = new SnCollection<SnChatUser>(...(value?.results ?? []));
        this.userHasAccess = this.channel.members.some((user) => this._currentUser.id === user.id);
        this._cd.detectChanges();
      });
  }
}
